import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useApprovedReports, useReportDownload } from '../api/hooks';
import type { ApprovedReportView } from '../api/types';
import {
  Button,
  DataTable,
  EmptyState,
  FormField,
  Select,
  TextInput,
  Modal,
  useToast,
} from '../components/DesignSystem';
import type { DataTableColumn } from '../components/DesignSystem';
import { DownloadIcon, EyeIcon } from '../components/icons';
import { PageHeader } from '../components/PageHeader';
import { errorMessage } from '../lib/errors';
import { formatCurrency, formatDate } from '../lib/format';

/** `/approved-reports` — the immutable record of approved royalty reports. */
export function ApprovedReportsPage() {
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const reports = useApprovedReports({ page, limit: 100 });
  const [search, setSearch] = useState('');
  const [show, setShow] = useState('');
  const [party, setParty] = useState('');
  const [quarter, setQuarter] = useState('');
  const rows = reports.data?.data ?? [];
  const reportingQuarter = (report: ApprovedReportView) => {
    const date = report.snapshot.periodEnd;
    return date ? `${date.slice(0, 4)} Q${Math.ceil(Number(date.slice(5, 7)) / 3)}` : '';
  };
  const visible = rows.filter(
    (row) =>
      (!search.trim() ||
        `${row.reportNumber} ${row.showName ?? ''}`
          .toLowerCase()
          .includes(search.trim().toLowerCase())) &&
      (!show || row.showName === show) &&
      (!party || row.snapshot.perParty.some((item) => item.partyId === party)) &&
      (!quarter || reportingQuarter(row) === quarter),
  );
  const parties = new Map(
    rows.flatMap((row) =>
      row.snapshot.perParty.map((item) => [item.partyId, item.partyName] as const),
    ),
  );
  const clearFilters = () => {
    setSearch('');
    setShow('');
    setParty('');
    setQuarter('');
  };
  const download = useReportDownload();

  const [detail, setDetail] = useState<ApprovedReportView | null>(null);

  async function handleDownload(report: ApprovedReportView) {
    try {
      const result = await download.mutateAsync(report.id);
      window.open(result.url, '_blank', 'noopener');
    } catch (error) {
      showToast(errorMessage(error, 'Could not fetch the download link'), 'error');
    }
  }

  const columns: DataTableColumn<ApprovedReportView>[] = [
    { key: 'reportNumber', header: 'Report #' },
    { key: 'showName', header: 'Show' },
    { key: 'seasonCode', header: 'Season' },
    {
      key: 'net',
      header: 'Net Receipts',
      align: 'right',
      render: (row) => formatCurrency(row.snapshot.netTotal, row.currency),
    },
    {
      key: 'due',
      header: 'Due to Pay',
      align: 'right',
      render: (row) => formatCurrency(row.snapshot.distributedTotal, row.currency),
    },
    {
      key: 'approvedBy',
      header: 'Approved By',
      render: (row) => row.approvedByName ?? row.approvedBy,
    },
    { key: 'approvedAt', header: 'Approved At', render: (row) => formatDate(row.approvedAt) },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <span className="rfp-icon-row">
          <button
            aria-label={`View ${row.reportNumber}`}
            className="rfp-iconbtn"
            onClick={() => setDetail(row)}
            title="View snapshot"
            type="button"
          >
            <EyeIcon />
          </button>
          <button
            aria-label={`Download ${row.reportNumber}`}
            className="rfp-iconbtn"
            disabled={download.isPending}
            onClick={() => handleDownload(row)}
            title="Download report"
            type="button"
          >
            <DownloadIcon />
          </button>
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        caption="Immutable snapshots of every approved reporting window, ready to share with counterparties."
        title="Approved Reports"
      />

      <section className="rfp-card" aria-label="Report filters">
        <h2 className="rfp-card__title">Filters</h2>
        <div className="rfp-filter-grid">
          <FormField htmlFor="report-search" label="Search reports">
            <TextInput
              id="report-search"
              placeholder="Search report name…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </FormField>
          <FormField htmlFor="report-show" label="Show">
            <Select id="report-show" value={show} onChange={(event) => setShow(event.target.value)}>
              <option value="">Any show</option>
              {Array.from(
                new Set(rows.map((row) => row.showName).filter((name): name is string => !!name)),
              ).map((name) => (
                <option key={name}>{name}</option>
              ))}
            </Select>
          </FormField>
          <FormField htmlFor="report-quarter" label="Quarter">
            <Select
              id="report-quarter"
              value={quarter}
              onChange={(event) => setQuarter(event.target.value)}
            >
              <option value="">Any quarter</option>
              {Array.from(new Set(rows.map(reportingQuarter).filter(Boolean)))
                .sort()
                .map((value) => (
                  <option key={value}>{value}</option>
                ))}
            </Select>
          </FormField>
          <FormField htmlFor="report-party" label="Party">
            <Select
              id="report-party"
              value={party}
              onChange={(event) => setParty(event.target.value)}
            >
              <option value="">Any party</option>
              {Array.from(parties).map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
          </FormField>
        </div>
        <div className="rfp-toolbar">
          <span className="rfp-muted-text">
            Filters apply to the {rows.length} reports on this page.
          </span>
          <Button type="button" variant="ghost" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      </section>
      <section className="rfp-card">
        {reports.isLoading ? (
          <p className="rfp-loading">Loading reports…</p>
        ) : reports.isError ? (
          <p className="rfp-inline-error" role="alert">
            Could not load approved reports — please retry.
          </p>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No approved reports yet"
            caption="Approve a waterfall report to see it here."
            action={
              <Link className="rfp-button rfp-button--primary" to="/report-simulations">
                Go to Report Simulation
              </Link>
            }
          />
        ) : (
          <DataTable
            columns={columns}
            emptyMessage="No reports match these filters"
            rowKey={(row) => row.id}
            rows={visible}
          />
        )}
      </section>

      <div className="rfp-toolbar">
        <Button
          type="button"
          variant="secondary"
          disabled={page === 1}
          onClick={() => {
            setPage(page - 1);
            clearFilters();
          }}
        >
          Previous page
        </Button>
        <span>Page {page}</span>
        <Button
          type="button"
          variant="secondary"
          disabled={!reports.data || page * 100 >= (reports.data.meta?.total ?? rows.length)}
          onClick={() => {
            setPage(page + 1);
            clearFilters();
          }}
        >
          Next page
        </Button>
      </div>

      {detail && (
        <Modal
          actions={
            <Button onClick={() => setDetail(null)} type="button" variant="secondary">
              Close
            </Button>
          }
          title={detail.reportNumber}
        >
          <div className="rfp-modal-wide">
            <p className="rfp-muted-text">
              {detail.showName ?? '—'}
              {detail.seasonCode ? ` · ${detail.seasonCode}` : ''} · period{' '}
              {formatDate(detail.snapshot.periodStart)} → {formatDate(detail.snapshot.periodEnd)}
            </p>
            <dl className="rfp-summary">
              <div className="rfp-summary__row">
                <dt>Gross</dt>
                <dd>{formatCurrency(detail.snapshot.grossTotal, detail.currency)}</dd>
              </div>
              <div className="rfp-summary__row">
                <dt>Distribution Commission</dt>
                <dd>{formatCurrency(detail.snapshot.commissionTotal ?? 0, detail.currency)}</dd>
              </div>
              <div className="rfp-summary__row">
                <dt>Distribution Expenses</dt>
                <dd>
                  {formatCurrency(detail.snapshot.distributionExpensesTotal ?? 0, detail.currency)}
                </dd>
              </div>
              <div className="rfp-summary__row">
                <dt>Residuals</dt>
                <dd>{formatCurrency(detail.snapshot.residualsTotal, detail.currency)}</dd>
              </div>
              <div className="rfp-summary__row">
                <dt>Recoupable Costs</dt>
                <dd>{formatCurrency(detail.snapshot.costsTotal ?? 0, detail.currency)}</dd>
              </div>
              <div className="rfp-summary__row">
                <dt>Net Receipts</dt>
                <dd>{formatCurrency(detail.snapshot.netTotal, detail.currency)}</dd>
              </div>
              <div className="rfp-summary__row">
                <dt>Distributable</dt>
                <dd>{formatCurrency(detail.snapshot.distributable, detail.currency)}</dd>
              </div>
              <div className="rfp-summary__row">
                <dt>Unallocated</dt>
                <dd>{formatCurrency(detail.snapshot.unallocatedTotal ?? 0, detail.currency)}</dd>
              </div>
              <div className="rfp-summary__row">
                <dt>Distributor Retained</dt>
                <dd>{formatCurrency(detail.snapshot.distributorRetained, detail.currency)}</dd>
              </div>
              {detail.snapshot.perParty.map((party) => (
                <div className="rfp-summary__row" key={party.partyId}>
                  <dt>{party.partyName}</dt>
                  <dd>{formatCurrency(party.total, detail.currency)}</dd>
                </div>
              ))}
              {detail.snapshot.sealed && (
                <>
                  <div className="rfp-summary__row">
                    <dt>Run revision</dt>
                    <dd>{detail.snapshot.sealed.runRevision}</dd>
                  </div>
                  <div className="rfp-summary__row">
                    <dt>Input hash</dt>
                    <dd>{detail.snapshot.sealed.inputHash}</dd>
                  </div>
                  <div className="rfp-summary__row">
                    <dt>Result hash</dt>
                    <dd>{detail.snapshot.sealed.resultHash}</dd>
                  </div>
                </>
              )}
            </dl>
          </div>
        </Modal>
      )}
    </>
  );
}
